/**
 * Módulo de Visualización Geográfica (MapPage).
 * Página principal para la consulta espacial de casos, con filtros avanzados
 * por riesgo, municipio y rango de fechas.
 */

import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import MapComponent from "./MapComponent";
import { Apiurl } from "../../shared/services/apirest";
import axios from "axios";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, LineChart, Line, CartesianGrid } from 'recharts';
import LogoGeo from "../../assets/img/LogoGeo.png";
import LogosFooter from "../../shared/components/LogosFooter";
import "../../assets/css/Dashboardadmain.css";

export default function MapPage() {
    // Estados para datos y filtrado
    const [allCasos, setAllCasos] = useState([]);
    const [filteredCasos, setFilteredCasos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [stats, setStats] = useState({ total: 0, alto: 0, medio: 0, bajo: 0 });
    const [weather, setWeather] = useState(null);
    const [showSidebar, setShowSidebar] = useState(window.innerWidth > 768);
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
    const [isNavCollapsed, setIsNavCollapsed] = useState(true);
    const handleNavCollapse = () => setIsNavCollapsed(!isNavCollapsed);

    // Ajuste responsivo para el sidebar
    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth <= 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Estado inicial de los filtros
    const [filters, setFilters] = useState({
        riesgo: "",
        municipio: "",
        fechaInicio: "",
        fechaFin: ""
    });

    /**
     * Carga inicial de todos los casos y clima del centro de Córdoba.
     */
    useEffect(() => {
        const fetchData = async () => {
            try {
                const res = await axios.get(`${Apiurl}casos/`);
                setAllCasos(res.data);
                setFilteredCasos(res.data);
                fetchWeather(8.75, -75.88);
            } catch (err) {
                console.error("Error al cargar datos del mapa", err);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, []);

    /**
     * Lógica de aplicación de filtros concurrentes.
     */
    useEffect(() => {
        let temp = allCasos;

        if (filters.riesgo) {
            temp = temp.filter(c => c.nivel_peligrosidad === filters.riesgo);
        }

        if (filters.fechaInicio) {
            temp = temp.filter(c => c.fecha_registro >= filters.fechaInicio);
        }

        if (filters.fechaFin) {
            temp = temp.filter(c => c.fecha_registro <= filters.fechaFin);
        }

        if (filters.municipio) {
            temp = temp.filter(c => c.paciente_municipio === filters.municipio);
        }

        setFilteredCasos(temp);
        calculateStats(temp);

    }, [filters, allCasos]);

    /**
     * Recalcula estadísticas rápidas basadas en el set de datos filtrado.
     */
    const calculateStats = (data) => {
        const newStats = { total: data.length, alto: 0, medio: 0, bajo: 0 };
        data.forEach(c => {
            const risk = (c.nivel_peligrosidad || "BAJO").toUpperCase();
            if (risk === "ALTO") newStats.alto++;
            else if (risk === "MEDIO") newStats.medio++;
            else newStats.bajo++;
        });
        setStats(newStats);
    };

    /**
     * Obtiene el clima actual del centro territorial.
     */
    const fetchWeather = async (lat, lon) => {
        try {
            const url = `https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lon}&current_weather=true&hourly=temperature_2m,relativehumidity_2m&daily=temperature_2m_max,temperature_2m_min&timezone=auto`;
            const res = await axios.get(url);
            setWeather(res.data.current_weather);
        } catch (e) {
            console.error("Fallo al obtener clima", e);
        }
    };

    // Prepara datos para el gráfico de evolución temporal
    const chartData = useMemo(() => {
        const dateCounts = {};
        filteredCasos.forEach(c => {
            const date = c.fecha_registro || "S/F";
            dateCounts[date] = (dateCounts[date] || 0) + 1;
        });
        return Object.keys(dateCounts).sort().map(d => ({ date: d, count: dateCounts[d] }));
    }, [filteredCasos]);

    // Extrae municipios únicos presentes en los datos para el filtro
    const uniqueMunicipios = useMemo(() => {
        const m = new Set(allCasos.map(c => c.paciente_municipio).filter(Boolean));
        return Array.from(m).sort();
    }, [allCasos]);

    const handleFilterChange = (e) => {
        setFilters({ ...filters, [e.target.name]: e.target.value });
    };

    const clearFilters = () => {
        setFilters({ riesgo: "", municipio: "", fechaInicio: "", fechaFin: "" });
    };

    return (
        <div style={{ minHeight: "100vh", display: "flex", flexDirection: "column", backgroundColor: "var(--bg-main)" }}>
            {/* Barra de Navegación Unificada */}
            <nav className="navbar navbar-expand-lg navbar-custom navbar-dark fixed-top shadow-sm">
                <div className="container-fluid">
                    <Link className="navbar-brand" to="/">
                        <img src={LogoGeo} alt="GeoHCX" className="nav-logo-img" />
                    </Link>
                    <button
                        className="navbar-toggler"
                        type="button"
                        onClick={handleNavCollapse}
                        aria-controls="navbarNav"
                        aria-expanded={!isNavCollapsed}
                        aria-label="Toggle navigation"
                    >
                        <span className="navbar-toggler-icon"></span>
                    </button>

                    <div className={`${isNavCollapsed ? 'collapse' : ''} navbar-collapse justify-content-center`} id="navbarNav">
                        <ul className="navbar-nav align-items-center">
                            <li className="nav-item"><Link className="nav-link" to="/" onClick={() => setIsNavCollapsed(true)}>INICIO</Link></li>
                            <li className="nav-item"><Link className="nav-link" to="/quienes-somos" onClick={() => setIsNavCollapsed(true)}>QUIÉNES SOMOS</Link></li>
                            <li className="nav-item"><Link className="nav-link" to="/indicadores" onClick={() => setIsNavCollapsed(true)}>INDICADORES</Link></li>
                            <li className="nav-item"><Link className="nav-link active" to="/mapa" onClick={() => setIsNavCollapsed(true)}>UBICACIÓN</Link></li>
                            <li className="nav-item"><Link className="nav-link" to="/consultar" onClick={() => setIsNavCollapsed(true)}>CONSULTAR</Link></li>
                            <li className="nav-item"><Link className="nav-link" to="/ambiental" onClick={() => setIsNavCollapsed(true)}>DATOS AMBIENTALES</Link></li>
                            <li className="nav-item ms-lg-4">
                                <Link to="/login" className="btn-login" onClick={() => setIsNavCollapsed(true)}>INICIAR SESIÓN</Link>
                            </li>
                        </ul>
                    </div>
                </div>
            </nav>

            <div className="d-flex" style={{ height: "calc(100vh - 80px)", marginTop: "80px", overflow: "hidden" }}>
                {/* Panel lateral de filtros y estadísticas */}
                <div
                    className={`bg-white shadow ${showSidebar ? "p-3" : "p-0"}`}
                    style={{
                        position: isMobile ? 'absolute' : 'relative',
                        zIndex: 1001,
                        height: '100%',
                        width: showSidebar ? (isMobile ? "100%" : "340px") : "0",
                        transition: "width 0.3s",
                        overflowY: "auto",
                        overflowX: 'hidden',
                        borderRight: "1px solid var(--border-stone)"
                    }}
                >
                    <div style={{ minWidth: '300px' }}>
                        <div className="d-flex justify-content-between align-items-center mb-4">
                            <h4 className="fw-bold m-0" style={{ color: "var(--text-deep)" }}>Tablero y Filtros</h4>
                            {isMobile && (
                                <button className="btn btn-sm btn-outline-secondary d-md-none" onClick={() => setShowSidebar(false)}>
                                    <i className="bi bi-x-lg"></i> Cerrar
                                </button>
                            )}
                        </div>

                        {/* SECCIÓN DE FILTROS */}
                        <div className="card border-0 shadow-sm mb-4" style={{ backgroundColor: "#fdfdfd" }}>
                            <div className="card-body p-3">
                                <h6 className="fw-bold mb-3" style={{ color: "var(--primary-moss)" }}><i className="bi bi-funnel"></i> Filtros de Visualización</h6>
                                <div className="row g-2">
                                    <div className="col-6">
                                        <label className="small fw-bold">Riesgo</label>
                                        <select name="riesgo" className="form-select form-select-sm" value={filters.riesgo} onChange={handleFilterChange}>
                                            <option value="">Todos</option>
                                            <option value="ALTO">Alto</option>
                                            <option value="MEDIO">Medio</option>
                                            <option value="BAJO">Bajo</option>
                                        </select>
                                    </div>
                                    <div className="col-6">
                                        <label className="small fw-bold">Municipio</label>
                                        <select name="municipio" className="form-select form-select-sm" value={filters.municipio} onChange={handleFilterChange}>
                                            <option value="">Todos</option>
                                            {uniqueMunicipios.map(m => (
                                                <option key={m} value={m}>{m}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="col-6">
                                        <label className="small fw-bold">Desde</label>
                                        <input type="date" name="fechaInicio" className="form-control form-control-sm" value={filters.fechaInicio} onChange={handleFilterChange} />
                                    </div>
                                    <div className="col-6">
                                        <label className="small fw-bold">Hasta</label>
                                        <input type="date" name="fechaFin" className="form-control form-control-sm" value={filters.fechaFin} onChange={handleFilterChange} />
                                    </div>
                                    <div className="col-12 text-end mt-2">
                                        <button className="btn btn-xs py-1 px-3 text-white" style={{ backgroundColor: "var(--risk-high)", border: "none" }} onClick={clearFilters}>Limpiar Filtros</button>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Clima Actual en Córdoba */}
                        <div className="card border-0 shadow-sm mb-4 text-white" style={{ background: 'linear-gradient(135deg, var(--primary-moss) 0%, #4a6316 100%)' }}>
                            <div className="card-body p-3">
                                <div className="d-flex justify-content-between">
                                    <div>
                                        <h6 className="card-title opacity-75 mb-0">Temperatura (Capital)</h6>
                                    </div>
                                    {weather && <h2 className="fw-bold m-0">{weather.temperature}°C</h2>}
                                </div>
                            </div>
                        </div>

                        {/* Estadísticas Rápidas */}
                        <div className="row g-2 mb-4">
                            <div className="col-4">
                                <div className="p-2 bg-light rounded text-center border">
                                    <h4 className="fw-bold text-dark m-0">{stats.total}</h4>
                                    <small style={{ fontSize: '0.7rem' }}>Total</small>
                                </div>
                            </div>
                            <div className="col-4">
                                <div className="p-2 rounded text-center border" style={{ backgroundColor: "rgba(239, 83, 80, 0.1)", borderColor: "var(--risk-high)" }}>
                                    <h4 className="fw-bold m-0" style={{ color: "var(--risk-high)" }}>{stats.alto}</h4>
                                    <small style={{ fontSize: '0.7rem' }}>Alto</small>
                                </div>
                            </div>
                            <div className="col-4">
                                <div className="p-2 rounded text-center border" style={{ backgroundColor: "rgba(255, 167, 38, 0.1)", borderColor: "var(--risk-medium)" }}>
                                    <h4 className="fw-bold m-0" style={{ color: "var(--risk-medium)" }}>{stats.medio}</h4>
                                    <small style={{ fontSize: '0.7rem' }}>Medio</small>
                                </div>
                            </div>
                        </div>

                        {/* Evolución de Casos Filtrados */}
                        <div className="mb-4">
                            <label className="fw-bold small text-muted mb-2">EVOLUCIÓN TEMPORAL</label>
                            <div style={{ width: '100%', height: 180 }}>
                                <ResponsiveContainer>
                                    <LineChart data={chartData}>
                                        <CartesianGrid strokeDasharray="3 3" vertical={false} />
                                        <XAxis dataKey="date" fontSize={10} tickFormatter={str => str.substring(5)} />
                                        <YAxis fontSize={10} allowDecimals={false} width={25} />
                                        <Tooltip contentStyle={{ fontSize: '12px', border: 'none', boxShadow: '0 2px 5px rgba(0,0,0,0.1)' }} />
                                        <Line type="monotone" dataKey="count" stroke="#6B8E23" strokeWidth={3} dot={{ r: 4, fill: '#6B8E23' }} activeDot={{ r: 6 }} />
                                    </LineChart>
                                </ResponsiveContainer>
                            </div>
                            <small className="text-muted d-block text-center mt-1" style={{ fontSize: '0.75rem' }}>* Datos sujetos a filtros aplicados</small>
                        </div>

                    </div>
                </div>

                {/* Área del Mapa Interactivo */}
                <div className="flex-grow-1 position-relative">
                    {isMobile && !showSidebar && (
                        <button
                            className="btn btn-primary shadow position-absolute"
                            style={{ top: '20px', left: '20px', zIndex: 1000, borderRadius: "50px", padding: "8px 16px", fontWeight: "bold", border: "2px solid white" }}
                            onClick={() => setShowSidebar(true)}
                        >
                            <i className="bi bi-funnel-fill me-2"></i> Filtros
                        </button>
                    )}
                    {!isMobile && (
                        <button
                            className="btn shadow position-absolute"
                            style={{ top: '10px', left: '10px', zIndex: 1000, backgroundColor: "var(--white)", color: "var(--primary-moss)" }}
                            onClick={() => setShowSidebar(!showSidebar)}
                        >
                            {showSidebar ? "◀" : "▶"}
                        </button>
                    )}

                    {loading ? (
                        <div className="d-flex w-100 h-100 justify-content-center align-items-center bg-light">
                            <div className="spinner-border text-success"></div>
                        </div>
                    ) : (
                        <MapComponent data={filteredCasos} />
                    )}
                </div>
            </div>

            <div style={{ flexShrink: 0 }}>
                <LogosFooter />
            </div>
        </div >
    );
}
